<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  attribute: {
    type: Object,
    required: true,
  },
  isEditingView: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update', 'delete']);
const { t } = useI18n();
const [showDropdown, toggleDropdown] = useToggle();

const selectedValues = computed(() => {
  const val = props.attribute.value;
  if (Array.isArray(val)) return val;
  if (val) return [val];
  return [];
});

const attributeListMenuItems = computed(() => {
  return (
    props.attribute.attributeValues?.map(value => ({
      label: value,
      value,
      action: 'select',
      isSelected: selectedValues.value.includes(value),
    })) || []
  );
});

const handleAttributeAction = async action => {
  const current = [...selectedValues.value];
  const idx = current.indexOf(action.value);
  if (idx >= 0) {
    current.splice(idx, 1);
  } else {
    current.push(action.value);
  }
  emit('update', current);
};

const removeTag = (value) => {
  const current = selectedValues.value.filter(v => v !== value);
  emit('update', current);
};
</script>

<template>
  <div class="ms-attr-root">
    <div v-on-clickaway="() => toggleDropdown(false)" class="ms-attr-container">
      <div v-if="selectedValues.length > 0" class="ms-tags-wrap">
        <span v-for="val in selectedValues" :key="val" class="ms-tag"
          @click.stop="isEditingView && removeTag(val)">
          {{ val }}
        </span>
        <button class="ms-add-btn" @click="toggleDropdown()">+</button>
      </div>
      <span v-else class="ms-placeholder" @click="toggleDropdown()">
        {{ t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.TRIGGER.SELECT') }}
      </span>
      <DropdownMenu v-if="showDropdown" :menu-items="attributeListMenuItems"
        show-search class="ms-dropdown"
        :class="{ 'ltr:right-0 rtl:left-0': !isEditingView, 'ltr:left-0 rtl:right-0': isEditingView }"
        @action="handleAttributeAction($event)" />
    </div>
    <div v-if="isEditingView" class="ms-actions">
      <Button variant="faded" color="slate" icon="i-lucide-pencil" size="xs"
        class="flex-shrink-0 opacity-0 group-hover/attribute:opacity-100 hover:no-underline"
        @click="toggleDropdown()" />
      <Button variant="faded" color="ruby" icon="i-lucide-trash" size="xs"
        class="flex-shrink-0 opacity-0 group-hover/attribute:opacity-100 hover:no-underline"
        @click="emit('delete')" />
    </div>
  </div>
</template>

<style scoped>
.ms-attr-root {
  display: flex;
  align-items: flex-start;
  width: 100%;
  min-width: 0;
  gap: 8px;
}
.ms-attr-container {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}
.ms-tags-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  width: 100%;
}
.ms-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  background: #DBEAFE;
  color: #1E40AF;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
  min-width: 60px;
  text-align: center;
}
.ms-tag:hover {
  background: #BFDBFE;
  text-decoration: line-through;
}
.ms-add-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #F1F5F9;
  color: #64748B;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  border: 1px dashed #CBD5E1;
  transition: all 0.15s ease;
}
.ms-add-btn:hover {
  background: #E2E8F0;
  color: #1E40AF;
  border-color: #93C5FD;
}
.ms-placeholder {
  min-width: 0;
  font-size: 14px;
  cursor: pointer;
  color: #94A3B8;
  padding: 8px 0;
  user-select: none;
  font-weight: 500;
}
.ms-placeholder:hover {
  color: #64748B;
}
.ms-dropdown {
  width: 12rem;
  margin-top: 8px;
  top: 100%;
}
.ms-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Dark mode */
:root.dark .ms-tag {
  background: rgba(59, 130, 246, 0.2);
  color: #93C5FD;
}
:root.dark .ms-tag:hover {
  background: rgba(59, 130, 246, 0.3);
}
:root.dark .ms-add-btn {
  background: rgba(255,255,255,0.05);
  color: #94A3B8;
  border-color: rgba(255,255,255,0.1);
}
:root.dark .ms-add-btn:hover {
  background: rgba(255,255,255,0.1);
  color: #93C5FD;
}
</style>
